import React from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { SessionProvider } from "./SessionProvider";
import { ToastProvider } from "./components/ui/ToastContext";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LoadScript } from "@react-google-maps/api";
import { SidebarProvider } from "./context/SidebarContext";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AccountAction from "./pages/AccountAction";
import UserProfile from "./pages/UserProfile";

import AdminDashboard from "./pages/admin/AdminDashboard";
import ManageUsers from "./pages/admin/ManageUsers";
import Audits from "./pages/admin/Audits";

import EventsDashboard from "./pages/EventsDashboard";
import ManageEvents from "./pages/ManageEvents";
import MicrositeBuilder from "./pages/MicrositeBuilder";
import MicrositePage from "./pages/MicrositePage";
import CheckinPage from "./components/events/CheckinPage";
import CheckInStatusBoard from "./components/events/CheckinStatusBoard";
import CheckinConsole from "./pages/checkinConsole/CheckinConsole";

import DocumentsDashboard from "./pages/DocumentsDashboard";
import DocumentsAndForms from "./pages/DocumentsAndForms";
import FormsBuilder from "./pages/FormsBuilder";
import ClientIntakeForm from "./pages/ClientIntakeForm";
import FormFiller from "./pages/FormFiller";

import Settings from "./pages/Settings";
import BillingPage from "./pages/BillingPage";
import RequireAuth from "./components/RequireAuth";

import ClientPortalLayout from "./pages/portal/ClientPortalLayout";
import ClientPortalDashboard from "./pages/portal/ClientPortalDashboard";
import ClientPortalInfo from "./pages/portal/ClientPortalInfo";
import ClientPortalDocuments from "./pages/portal/ClientPortalDocuments";
import ClientPortalFormsTodo from "./pages/portal/ClientPortalFormsTodo";
import ClientPortalFormsCompleted from "./pages/portal/ClientPortalFormsCompleted";
import FormFillerPortal from "./pages/FormFillerPortal";

import InviteGate from "./pages/portal/InviteGate";
import RequireClient from "./pages/portal/RequireClient";
import { ActAsClientProvider } from "./pages/portal/ActAsClientContext";
import ClientPortalRecover from "./pages/portal/ClientPortalRecover";
import InviteInvalid from "./pages/portal/InviteInvalid";

// ✅ NEW: post-login router
import PostLoginRouter from "./pages/PostLoginRouter";

// ✅ NEW: subscription banner manager (persists across routes)
import SubscriptionActivationManager from "./components/billing/SubscriptionActivationManager";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_LIBRARIES = ["places"];

function App() {
  return (
    <Router>
      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={GOOGLE_MAPS_LIBRARIES}>
        <SessionProvider>
          <ToastProvider>
            <SidebarProvider>
              <DndProvider backend={HTML5Backend}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  {/* ✅ Banner-only manager (does NOT show gate; skips /post-login) */}
                  <SubscriptionActivationManager />

                  <Routes>
                    <Route path="/" element={<Navigate to="/login" replace />} />

                    <Route path="/login" element={<Login />} />
                    <Route path="/account-action" element={<AccountAction />} />
                    <Route path="/profile" element={<UserProfile />} />

                    {/* ✅ NEW */}
                    <Route
                      path="/post-login"
                      element={
                        <RequireAuth>
                          <PostLoginRouter />
                        </RequireAuth>
                      }
                    />

                    <Route path="/dashboard" element={<Dashboard />} />

                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/manage-users" element={<ManageUsers />} />
                    <Route path="/admin/audits" element={<Audits />} />

                    <Route path="/events" element={<EventsDashboard />} />
                    <Route path="/events/dashboard" element={<EventsDashboard />} />
                    <Route path="/events/manage" element={<ManageEvents />} />
                    <Route path="/microsite-builder/:eventId" element={<MicrositeBuilder />} />
                    <Route path="/:organizationId/:eventId" element={<MicrositePage />} />

                    <Route path="/checkin" element={<CheckinPage />} />

                    <Route
                      path="/checkin-console/*"
                      element={
                        <RequireAuth>
                          <CheckinConsole />
                        </RequireAuth>
                      }
                    />

                    <Route
                      path="/checkin/status"
                      element={
                        <RequireAuth>
                          <CheckInStatusBoard />
                        </RequireAuth>
                      }
                    />

                    <Route path="/documents" element={<DocumentsDashboard />} />
                    <Route path="/documents/dashboard" element={<DocumentsDashboard />} />
                    <Route path="/documents/annotations" element={<DocumentsAndForms />} />
                    <Route path="/documents/builder" element={<FormsBuilder />} />
                    <Route path="/documents/client-intake" element={<ClientIntakeForm />} />
                    <Route path="/forms/fill/:formId" element={<FormFiller />} />

                    <Route
                      path="/settings"
                      element={
                        <RequireAuth>
                          <Settings />
                        </RequireAuth>
                      }
                    />

                    <Route
                      path="/settings/billing"
                      element={
                        <RequireAuth>
                          <BillingPage />
                        </RequireAuth>
                      }
                    />

                    <Route path="/portal/invite" element={<InviteGate />} />
                    <Route path="/portal/invite-invalid" element={<InviteInvalid />} />
                    <Route path="/portal/recover" element={<ClientPortalRecover />} />

                    <Route
                      path="/portal"
                      element={
                        <ActAsClientProvider>
                          <RequireClient>
                            <ClientPortalLayout />
                          </RequireClient>
                        </ActAsClientProvider>
                      }
                    >
                      <Route index element={<ClientPortalDashboard />} />
                      <Route path="documents" element={<ClientPortalDocuments />} />
                      <Route path="forms" element={<ClientPortalFormsTodo />} />
                      <Route path="forms/completed" element={<ClientPortalFormsCompleted />} />
                      <Route path="info" element={<ClientPortalInfo />} />
                      <Route path="forms/fill/:formId" element={<FormFillerPortal />} />
                    </Route>
                  </Routes>
                </LocalizationProvider>
              </DndProvider>
            </SidebarProvider>
          </ToastProvider>
        </SessionProvider>
      </LoadScript>
    </Router>
  );
}

export default App;